import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute } from "@tanstack/react-router";
import { PriorityServiceAreaPage } from "@/components/PriorityServiceAreaPage";
import { seoHead, serviceSchema, localBusinessSchema } from "@/lib/seo";
import { faqSchema } from "@/components/FAQAccordion";

const FAQS = [
  { q: "How much do brake pads cost in Poplar?", a: "Front pad replacements typically start from £95–£180 fitted depending on the car. We'll quote before any work — no surprises." },
  { q: "How long does it take to replace brake pads and discs?", a: "Most cars: 1–2 hours for pads, 2–3 hours for pads and discs. Drop off on the way to work and collect later the same day." },
  { q: "My brakes are squealing — is it dangerous?", a: "A light squeal can be brake dust or worn pads. A constant grinding noise usually means metal-on-metal — stop driving and call us. We'll inspect for free and quote clearly." },
  { q: "Do you fit OE-quality brake parts?", a: "Yes — we use Brembo, ATE, Pagid, TRW and other reputable brands. Cheaper aftermarket pads are available if budget is tight." },
];

export const Route = createFileRoute("/poplar/brakes")({
  head: () => seoHead({
    path: "/poplar/brakes",
    title: "Brake Repair in Poplar, E14 — Pads, Discs & Calipers | Woodseer Autos",
    description: `Honest brake repair in Poplar, E14. Pads, discs and calipers fitted same-day where possible. Free inspection. Call ${NAP.phone}.`,
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Poplar", path: "/areas/poplar" },
      { name: "Brakes", path: "/poplar/brakes" },
    ],
    jsonLd: [
      localBusinessSchema({ serviceCoverage: ["Poplar"] }),
      serviceSchema({
        name: "Brake Repair in Poplar",
        serviceType: "Brake repair",
        description: "Brake pad, disc, caliper and fluid replacement for drivers in Poplar, E14. Free inspection and clear quote before work starts.",
        areaServed: "Poplar, London",
        url: "https://woodseerautos.co.uk/poplar/brakes",
      }),
      faqSchema(FAQS) as Record<string, unknown>,
    ],
  }),
  component: () => (
    <PriorityServiceAreaPage
      areaSlug="poplar"
      areaName="Poplar"
      serviceSlug="brakes"
      serviceName="Brake Repair"
      parentServiceSlug="mechanical-repairs"
      hero="Squealing, grinding or a soft pedal? Our Poplar workshop handles brake pads, discs, calipers and fluid replacement on most makes and models — usually same-day."
      localBenefit="Free brake inspection — quote before any work starts"
      whatsIncluded={[
        "Full brake inspection (pads, discs, calipers, hoses, fluid)",
        "OE-quality pads and discs fitted",
        "Caliper service or replacement where needed",
        "Brake fluid top-up or full bleed",
        "Pad bedding-in advice before you leave",
        "Written report of remaining pad life",
      ]}
      commonIssues={[
        "Squealing when braking (worn pads or dust)",
        "Grinding noise (pads worn through to metal)",
        "Pulling to one side under braking",
        "Soft or spongy pedal (air in lines or worn fluid)",
        "Vibration through the wheel (warped discs)",
        "Handbrake won't hold on hills",
      ]}
      process={[
        { step: "Free inspection", desc: "We check pad thickness, disc condition and fluid level before quoting." },
        { step: "Clear quote", desc: "Parts and labour up front — no surprises, no upsell." },
        { step: "Fitted same day where possible", desc: "Pads in 1–2 hours, discs in 2–3. We'll call when ready." },
        { step: "Road test & bedding-in", desc: "Brakes tested under load and you get advice on bedding them in." },
      ]}
      whyUs={[
        "OE-quality pads and discs as standard",
        "Free inspection — no obligation",
        "Walk to Poplar DLR while we work",
        "Honest about wear life — no early replacements",
      ]}
      faqs={FAQS}
    />
  ),
});
